import { z } from '../zod';

// The dashboard order bump editor's wire contract (PascalCase enums, camelCase
// fields), served under /api/v1/sellers/:sellerId/pages/:pageId/order-bumps.

// Sent whole rather than patched field by field: a rule only means anything as
// a comparison together with the ids it watches, so a partial write could
// persist a half-rule the checkout would have to guess at.
export const OrderBumpEditorShowHideLogicInputValidationSchema = z.object({
  enabled: z.boolean().meta({ description: 'Whether the rule is on.' }),
  // Absent on every rule saved before bumps could be watched, which reads as
  // the page's own selection.
  subject: z
    .enum(['PRICE', 'TICKET', 'ORDER_BUMP'])
    .nullish()
    .meta({
      description: 'What the rule watches. Defaults to PRICE on a checkout and TICKET on an event.',
    }),
  comparison: z.enum(['IS', 'IS_NOT']).default('IS'),
  // Empty while the seller has ticked the box but not yet picked a price. An
  // incomplete rule is stored, not rejected - it simply never hides anything.
  priceIds: z
    .array(z.string().trim().min(1))
    .optional()
    .meta({ description: 'The page product prices to watch, on a checkout.' }),
  // The event form of the rule: the add-on shows while any of these tickets
  // is in the basket (or none is, for IS_NOT).
  ticketTypeIds: z
    .array(z.string().trim().min(1))
    .optional()
    .meta({ description: 'The ticket types to watch, on an event.' }),
  // Loops between bumps are stored as they are, as variant rules are, and the
  // checkout hides every bump on one.
  orderBumpProductIds: z
    .array(z.string().trim().min(1))
    .optional()
    .meta({ description: 'Product IDs of other order bumps on this page to watch.' }),
  labels: z
    .array(z.object({ id: z.string().trim().min(1), name: z.string().max(500) }))
    .optional()
    .meta({
      description:
        'What the watched IDs were called when saved, for the editor to fall back on once one is deleted. Never read by the checkout.',
    }),
});

export type OrderBumpEditorShowHideLogicInput = z.infer<
  typeof OrderBumpEditorShowHideLogicInputValidationSchema
>;

// The page-scoped settings. Placement copy and price overrides were retired - a
// bump reads its title, description and price off the product on every page -
// so the Page model keeps those fields but nothing writes or reads them.
export const OrderBumpEditorSettingsBodyValidationSchema = z
  .object({
    enabled: z.boolean().meta({ description: 'Whether the placement is offered.' }),
    preselected: z.boolean().meta({ description: 'Whether it is ticked when the page loads.' }),
    hidden: z
      .boolean()
      .meta({ description: 'Hidden unless the page URL reveals it with ?show_order_bumps=.' }),
    showHideLogic: OrderBumpEditorShowHideLogicInputValidationSchema,
    allowQuantity: z.boolean().meta({ description: 'Whether the card offers a quantity stepper.' }),
    shortenDescription: z
      .boolean()
      .meta({ description: 'Whether the card clamps a long description.' }),
  })
  .partial();

// `placement` lets a duplicate arrive with its original's settings in the same
// write, rather than attaching bare and patching after.
export const OrderBumpEditorAttachBodyValidationSchema = z.object({
  productId: z
    .string()
    .trim()
    .min(1)
    .meta({ description: 'ID of the order bump product to place.' }),
  placement: OrderBumpEditorSettingsBodyValidationSchema.optional(),
});

export const OrderBumpEditorReorderBodyValidationSchema = z.object({
  productIds: z
    .array(z.string())
    .meta({
      description:
        'Every order bump product ID on the page, exactly once, in the new display order.',
    }),
});
